import { useState } from "react";
import DatePickerField from "../molecules/DatePickerField";
import CalendarButton from "../atoms/CalendarButton";
import ActionButton from "../atoms/ActionButton";

// YYYYMMDD 文字列 → Date オブジェクト
const parseYYYYMMDD = (str) =>
  new Date(
    parseInt(str.slice(0, 4), 10),
    parseInt(str.slice(4, 6), 10) - 1,
    parseInt(str.slice(6, 8), 10),
  );

// Date オブジェクト → YYYYMMDD 文字列
const formatYYYYMMDD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}${m}${day}`;
};

// YYYYMMDD → YYYY/MM/DD に変換して表示用にする
const formatDateDisplay = (yyyymmdd) =>
  `${yyyymmdd.slice(0, 4)}/${yyyymmdd.slice(4, 6)}/${yyyymmdd.slice(6, 8)}`;

/**
 * 操作日を基準にデフォルトのロック範囲を算出する。
 * 基準軸は「操作を行った実際の月」ではなく「締め切ろうとしている期間（16日〜末日）が
 * どの月に属するか」である点に注意。
 * - 当月11〜25日に操作 → 当月1〜15日をデフォルトにする
 * - 当月26日〜当月末日に操作 → 当月16日〜当月末日をデフォルトにする（対象月は当月自身）
 * - 翌月1〜10日に操作 → 前月16日〜前月末日をデフォルトにする（対象月は前月）
 * 境界を実運用日（16日・翌月1〜2日）からあえて広めに取り、長期休暇による操作の後ろ倒しを吸収する。
 */
const getDefaultLockRange = () => {
  const now = new Date();
  const day = now.getDate();
  const y = now.getFullYear();
  const m = now.getMonth();

  if (day >= 11 && day <= 25) {
    return {
      start: formatYYYYMMDD(new Date(y, m, 1)),
      end: formatYYYYMMDD(new Date(y, m, 15)),
    };
  }
  if (day >= 26) {
    // 当月16日〜当月末日（当月末日は「翌月0日目」として算出する）
    return {
      start: formatYYYYMMDD(new Date(y, m, 16)),
      end: formatYYYYMMDD(new Date(y, m + 1, 0)),
    };
  }
  // 前月16日〜前月末日（前月末日は「今月0日目」として算出する）
  return {
    start: formatYYYYMMDD(new Date(y, m - 1, 16)),
    end: formatYYYYMMDD(new Date(y, m, 0)),
  };
};

/**
 * LockPeriodForm - 締め切り（ロック）登録フォーム
 *
 * 日付範囲を指定し、確認ステップを経てロックを登録する。
 * デフォルト範囲は操作日から自動判定する（手動で上書き可能）。
 * 有効な部分解除（RelockedAt IS NULL の RecordUnlock）がある場合は、
 * 確認ステップで一覧を表示し、締め切りと同時に再ロックされることを明示する。
 *
 * @param {function} onRegister   - (startDate, endDate) => Promise を呼ぶ登録ハンドラ
 * @param {boolean}  disabled     - 登録処理中の非活性フラグ
 * @param {object[]} activeRelocks - 有効な部分解除一覧（{ id, shopName, regiName, YYYYMMDD, UnlockedBy }[]）
 */
function LockPeriodForm({ onRegister, disabled, activeRelocks = [] }) {
  const [range] = useState(getDefaultLockRange);
  const [startDate, setStartDate] = useState(range.start);
  const [endDate, setEndDate] = useState(range.end);
  const [confirming, setConfirming] = useState(false);

  const invalidRange = startDate > endDate;

  const handleExecute = async () => {
    await onRegister(startDate, endDate);
    setConfirming(false);
  };

  return (
    <div className="border border-gray-300 rounded p-4 mb-6">
      <h2 className="text-lg font-bold mb-3">締め切り登録</h2>
      <div className="flex items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-base text-gray-600">開始日</span>
          <DatePickerField
            selected={parseYYYYMMDD(startDate)}
            onChange={(d) => setStartDate(formatYYYYMMDD(d))}
            customInput={<CalendarButton yyyyValue={startDate} showDay />}
            dateFormat="yyyy/MM/dd"
            locale="ja"
            dateFormatCalendar="yyyy MMMM"
            calendarClassName="datepicker-date-picker"
            popperPlacement="bottom-start"
            popperProps={{ strategy: "fixed" }}
            enableTabLoop={false}
          />
        </div>
        <span className="text-gray-400">〜</span>
        <div className="flex items-center gap-2">
          <span className="text-base text-gray-600">終了日</span>
          <DatePickerField
            selected={parseYYYYMMDD(endDate)}
            onChange={(d) => setEndDate(formatYYYYMMDD(d))}
            customInput={<CalendarButton yyyyValue={endDate} showDay />}
            dateFormat="yyyy/MM/dd"
            locale="ja"
            dateFormatCalendar="yyyy MMMM"
            calendarClassName="datepicker-date-picker"
            popperPlacement="bottom-start"
            popperProps={{ strategy: "fixed" }}
            enableTabLoop={false}
          />
        </div>
      </div>

      {invalidRange && (
        <p className="text-red-600 text-base mb-3">
          開始日は終了日より前の日付にしてください。
        </p>
      )}

      {!confirming ? (
        <ActionButton
          label="締め切る"
          onClick={() => setConfirming(true)}
          disabled={disabled || invalidRange}
          paddingClass="px-8 py-2 my-2"
          textSizeClass="text-lg font-bold"
        />
      ) : (
        <div className="bg-gray-50 border border-gray-300 rounded p-4">
          <p className="text-base text-gray-700 mb-4">
            {formatDateDisplay(startDate)} 〜 {formatDateDisplay(endDate)} の期間を締め切ります。
            <br />
            この期間のデータは編集できなくなります。よろしいですか？
          </p>

          {/* 有効な部分解除がある場合は一覧を表示して再ロックされることを明示する */}
          {activeRelocks.length > 0 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded">
              <p className="text-base text-amber-800 font-medium mb-2">
                有効な部分解除が {activeRelocks.length} 件あります。締め切りと同時にすべて再ロックされます。
              </p>
              <ul className="text-base text-gray-700 space-y-0.5">
                {activeRelocks.map((u) => (
                  <li key={u.id} className="flex gap-2">
                    <span>・</span>
                    <span>
                      {u.shopName}　{u.regiName}　{formatDateDisplay(u.YYYYMMDD)}
                      {u.UnlockedBy ? `（解除者: ${u.UnlockedBy}）` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-4">
            <ActionButton
              label="キャンセル"
              onClick={() => setConfirming(false)}
              disabled={disabled}
              paddingClass="px-5 py-2"
              textSizeClass="text-base"
            />
            <ActionButton
              label={activeRelocks.length > 0 ? "再ロックして締め切る" : "実行する"}
              onClick={handleExecute}
              disabled={disabled}
              paddingClass="px-5 py-2"
              textSizeClass="text-base"
              colorClass="bg-gray-700 text-white"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default LockPeriodForm;
